import type { ArticleNode } from 'data-layer';

/**
 * Normalizes a string for case-insensitive matching
 * Also handles OS name variations from different sources
 *
 * **Detection utilities return tokens (examples):**
 * - "mac", "windows", "ios", "android", "linux", "unknown"
 * - "chrome", "firefox", "safari", "edge", "unknown"
 *
 * **Contentful platformKeys:**
 * - "MacChrome", "WinFirefox", "MacSafari", etc.
 *
 * @param key - The string to normalize
 * @returns Normalized lowercase string with OS name variations replaced
 *
 * @example
 * normalizeMatchKey('MacChrome') // 'macchrome'
 * normalizeMatchKey('mac chrome') // 'mac chrome'
 * normalizeMatchKey('windows firefox') // 'win firefox'
 * normalizeMatchKey('macOS Safari') // 'mac safari'
 */
export function normalizeMatchKey(key: string): string {
  return (
    key
      .toLowerCase()
      .trim()
      // Handle legacy "macOS" from Contentful (no space)
      .replace(/\bmacos\b/g, 'mac')
      // Handle "windows" -> "win" for matching with Contentful "Win*" platformKeys
      .replace(/\bwindows\b/g, 'win')
  );
}

export interface FilterDownloadStepsParams {
  articles: ArticleNode[];
  operatingSystem: string;
  browser: string;
}

/**
 * Filters download-steps articles based on operating system and browser.
 * Matches articles by their platformKey property using robust normalization.
 *
 * **How it works:**
 * 1. Combines OS and browser into a single string (e.g., "mac chrome")
 * 2. Normalizes both environment key and article platformKey (lowercase, macOS → mac, windows → win)
 * 3. Removes all whitespace for strict matching (e.g., "mac chrome" → "macchrome")
 * 4. Checks for exact match between normalized environment and platformKey
 * 5. Returns first matching article or falls back to "Others" article
 *
 * **Platform Matching:**
 * - OS/browser tokens: "mac", "windows", "chrome", "firefox", etc. (from detection utilities / hooks)
 * - Articles have platformKey like "MacChrome", "WinFirefox", "Others", etc.
 * - Both are normalized: "windows" → "win", "MacChrome" → "macchrome"
 * - Exact match: normalized environment must equal normalized platformKey
 * - Falls back to "Others" article if no match found
 *
 * @param params.articles - Array of download-steps articles
 * @param params.operatingSystem - Detected OS token (e.g., "mac", "windows")
 * @param params.browser - Detected browser token (e.g., "chrome", "firefox")
 * @returns The matching article or null if no match and no fallback
 *
 * @example
 * const article = filterDownloadSteps({
 *   articles: downloadStepsArticles,
 *   operatingSystem: 'mac',  // from detection utilities (e.g. `useDeviceInfo` or token helpers)
 *   browser: 'chrome'        // from detection utilities (e.g. `useDeviceInfo` or token helpers)
 * });
 * // Returns article with platformKey "MacChrome"
 */
export function filterDownloadSteps({
  articles,
  operatingSystem,
  browser,
}: FilterDownloadStepsParams): ArticleNode | null {
  // If either OS or browser is empty, we can't match properly - use fallback
  if (!operatingSystem || !browser) {
    return articles.find(article => normalizeMatchKey(article.platformKey || '').includes('others')) || null;
  }

  // Combine OS and browser for matching (e.g., "Mac OS Chrome" → "macchrome")
  const environmentKey = `${operatingSystem} ${browser}`;
  const normalizedEnv = normalizeMatchKey(environmentKey).replace(/\s+/g, '');

  // Find matching article or "Others" fallback
  let matchingArticle: ArticleNode | undefined;
  let othersArticle: ArticleNode | undefined;

  for (const article of articles) {
    const platformKey = normalizeMatchKey(article.platformKey || '').replace(/\s+/g, '');

    if (platformKey.includes('others')) {
      othersArticle = article;
      continue;
    }

    // Only set matching article if we haven't found one yet (first match wins)
    if (!matchingArticle) {
      // Check if normalized environment matches platform key
      if (platformKey === normalizedEnv) {
        matchingArticle = article;
      }
    }
  }

  // Use matching article or fallback to "Others"
  return matchingArticle || othersArticle || null;
}
